"use client";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React from "react";

// ============================================================================
// Root Section
// ============================================================================

export const CallDocumentatorSection = () => {
  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <SectionBackground />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <BrandReveal />
        <div className="mt-12 lg:mt-20">
          <DocumentationScene />
        </div>
        <div className="mt-14 lg:mt-24">
          <CapabilityGrid />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band with hairlines
// ============================================================================

const SectionBackground = () => (
  <>
    <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />
  </>
);

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("callDocumentator");
  return (
    <div className="flex flex-col items-center text-center">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <DocIcon className="h-3.5 w-3.5" />
        Call Documentator
      </p>

      <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white max-w-3xl">
        {t("tagline")}
      </h2>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — Documentation Scene (transcript ⊕ summary)
// ============================================================================

const DocumentationScene = () => {
  return (
    <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[520px]">
        <TranscriptPane />
        <SummaryPane />
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Transcript pane (left)
// ----------------------------------------------------------------------------

type TranscriptLineCfg = {
  key: 1 | 2 | 3 | 4;
  speaker: "lead" | "rep";
  delay: number;
};

const TRANSCRIPT_LINES: TranscriptLineCfg[] = [
  { key: 1, speaker: "lead", delay: 0.4 },
  { key: 2, speaker: "rep", delay: 1.6 },
  { key: 3, speaker: "lead", delay: 2.8 },
  { key: 4, speaker: "rep", delay: 4.0 },
];

const TranscriptPane = () => {
  const t = useTranslations("callDocumentator");
  const [seconds, setSeconds] = React.useState(0);

  React.useEffect(() => {
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const mm = Math.floor(seconds / 60).toString().padStart(2, "0");
  const ss = (seconds % 60).toString().padStart(2, "0");

  return (
    <div className="lg:col-span-7 border-b lg:border-b-0 lg:border-r border-neutral-800 bg-neutral-900/40 p-5 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          <span className="text-[10px] font-semibold tracking-wider text-emerald-400 uppercase">
            {t("live_label")}
          </span>
        </div>
        <span className="text-[10px] font-mono tabular-nums text-neutral-400">
          {mm}:{ss}
        </span>
      </div>

      {/* Header card */}
      <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-3 mb-4">
        <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider mb-1">
          {t("call_with")}
        </div>
        <div className="text-sm font-semibold text-neutral-100">
          Marcus Weber
        </div>
        <div className="text-[11px] text-neutral-500">{t("active_role")}</div>
      </div>

      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium mb-3 flex items-center gap-1.5">
        <WaveformIcon />
        <span>{t("transcript_label")}</span>
      </div>

      <ul className="space-y-2 flex-1">
        {TRANSCRIPT_LINES.map((line) => (
          <TranscriptLine
            key={line.key}
            speaker={line.speaker}
            speakerLabel={
              line.speaker === "lead" ? t("speaker_lead") : t("speaker_rep")
            }
            text={t(`transcript_line_${line.key}`)}
            delay={line.delay}
          />
        ))}
      </ul>
    </div>
  );
};

const TranscriptLine = ({
  speaker,
  speakerLabel,
  text,
  delay,
}: {
  speaker: "lead" | "rep";
  speakerLabel: string;
  text: string;
  delay: number;
}) => {
  return (
    <motion.li
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay, ease: "easeOut" }}
      className={cn(
        "flex items-start gap-2.5",
        speaker === "rep" && "flex-row-reverse"
      )}
    >
      <span
        className={cn(
          "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold uppercase",
          speaker === "lead"
            ? "bg-neutral-800 text-neutral-400 ring-1 ring-neutral-700"
            : "bg-red-500/15 text-red-300 ring-1 ring-red-500/30"
        )}
      >
        {speaker === "lead" ? "L" : "R"}
      </span>
      <div
        className={cn(
          "flex-1 max-w-[80%] rounded-lg px-3 py-2 border",
          speaker === "lead"
            ? "bg-neutral-900 border-neutral-800"
            : "bg-red-950/30 border-red-900/40"
        )}
      >
        <div
          className={cn(
            "text-[10px] uppercase tracking-wider font-semibold mb-1",
            speaker === "lead" ? "text-neutral-500" : "text-red-300"
          )}
        >
          {speakerLabel}
        </div>
        <p className="text-sm text-neutral-200 leading-relaxed">{text}</p>
      </div>
    </motion.li>
  );
};

// ----------------------------------------------------------------------------
// Summary pane (right)
// ----------------------------------------------------------------------------

const SummaryPane = () => {
  const t = useTranslations("callDocumentator");
  return (
    <div className="lg:col-span-5 relative overflow-hidden p-5 bg-neutral-950">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <FlameIcon className="h-3.5 w-3.5 text-red-500" />
          <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
            {t("summary_label")}
          </span>
        </div>
        <span
          className="text-[10px] font-semibold px-1.5 py-0.5 rounded border text-emerald-400 bg-emerald-950/60 border-emerald-900"
        >
          ✓ {t("outcome_chip")}
        </span>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 5.2, ease: "easeOut" }}
        className="rounded-xl border border-neutral-800 bg-neutral-900 p-4 relative overflow-hidden"
      >
        <div className="absolute inset-x-0 -top-px h-px bg-neutral-800" />
        <ul className="space-y-2">
          {[1, 2, 3].map((i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-sm text-neutral-300"
            >
              <span className="text-red-500 text-[14px] leading-none mt-0.5">
                →
              </span>
              <span>{t(`summary_line_${i}`)}</span>
            </li>
          ))}
        </ul>

        <div className="mt-4 pt-3 border-t border-neutral-800">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium mb-2">
            {t("tags_label")}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[1, 2, 3].map((i) => (
              <span
                key={i}
                className="text-[10px] font-medium text-neutral-400 bg-neutral-800/80 border border-neutral-700 rounded px-2 py-0.5"
              >
                {t(`tag_${i}`)}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-4 text-[9px] text-neutral-500 flex items-center gap-1">
          <span className="h-1 w-1 rounded-full bg-red-500" />
          {t("footer_meta")}
        </div>
      </motion.div>
    </div>
  );
};

// ============================================================================
// Block 3 — Capability Grid
// ============================================================================

type TileConfig = { key: string; icon: React.ReactNode };

const TILES: TileConfig[] = [
  { key: "t1", icon: <IconTranscript /> },
  { key: "t2", icon: <IconSummary /> },
  { key: "t3", icon: <IconOutcome /> },
  { key: "t4", icon: <IconArchive /> },
];

const CapabilityGrid = () => {
  const t = useTranslations("callDocumentator");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TILES.map((tile) => (
        <CapabilityTile
          key={tile.key}
          icon={tile.icon}
          label={t(`${tile.key}_label`)}
          desc={t(`${tile.key}_desc`)}
        />
      ))}
    </div>
  );
};

const CapabilityTile = ({
  icon,
  label,
  desc,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
}) => (
  <div className="relative rounded-xl border border-neutral-800 bg-neutral-900/50 p-5">
    <div className="relative">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        {icon}
      </div>
      <h3 className="mt-3 text-sm md:text-base font-semibold text-white">
        {label}
      </h3>
      <p className="mt-1 text-xs md:text-sm text-neutral-400 leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

// ============================================================================
// Icons
// ============================================================================

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 2.5c1.3 3.1 3.8 4.6 4.7 6.9.9 2.3.3 4.7-1.4 6.4-.6.6-1.3 1-2.1 1.2.5-.9.6-2 .2-3-.5-1.2-1.7-1.9-2.9-1.9-.3 1-.9 1.9-1.7 2.5-1.7 1.3-2.3 3.5-1.6 5.4.5 1.4 1.6 2.6 3 3-3.7-.2-7-3.1-7.4-6.9-.4-4.1 2.4-7.7 5.5-10.1C10.3 4.7 11.4 3.6 12 2.5z" />
    </svg>
  );
}

function DocIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M4 5a2 2 0 0 1 2-2h9l5 5v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5Z" />
      <path d="M15 3v4a2 2 0 0 0 2 2h4" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  );
}

function WaveformIcon() {
  return (
    <svg
      width="14"
      height="10"
      viewBox="0 0 14 10"
      fill="none"
      className="text-emerald-500 flex-shrink-0"
      aria-hidden
    >
      <rect x="0" y="3" width="1.5" height="4" fill="currentColor" rx="0.5">
        <animate
          attributeName="height"
          values="4;7;4"
          dur="0.9s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="y"
          values="3;1.5;3"
          dur="0.9s"
          repeatCount="indefinite"
        />
      </rect>
      <rect x="3" y="2" width="1.5" height="6" fill="currentColor" rx="0.5">
        <animate
          attributeName="height"
          values="6;3;6"
          dur="0.7s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="y"
          values="2;3.5;2"
          dur="0.7s"
          repeatCount="indefinite"
        />
      </rect>
      <rect x="6" y="1" width="1.5" height="8" fill="currentColor" rx="0.5">
        <animate
          attributeName="height"
          values="8;4;8"
          dur="1.1s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="y"
          values="1;3;1"
          dur="1.1s"
          repeatCount="indefinite"
        />
      </rect>
      <rect x="9" y="2" width="1.5" height="6" fill="currentColor" rx="0.5">
        <animate
          attributeName="height"
          values="6;2;6"
          dur="0.8s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="y"
          values="2;4;2"
          dur="0.8s"
          repeatCount="indefinite"
        />
      </rect>
      <rect x="12" y="3" width="1.5" height="4" fill="currentColor" rx="0.5">
        <animate
          attributeName="height"
          values="4;6;4"
          dur="1s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="y"
          values="3;2;3"
          dur="1s"
          repeatCount="indefinite"
        />
      </rect>
    </svg>
  );
}

function IconTranscript() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M3 12a4 4 0 0 0 4 4h1l3 3v-3h6a4 4 0 0 0 4-4V8a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4z" />
      <path d="M8 10h8M8 13h5" />
    </svg>
  );
}

function IconSummary() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 9h8M8 13h8M8 17h5" />
    </svg>
  );
}

function IconOutcome() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12l3 3 5-6" />
    </svg>
  );
}

function IconArchive() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
      <path d="M10 13h4" />
      <circle cx="18" cy="18" r="3" />
      <path d="m20.5 20.5-1.5-1.5" />
    </svg>
  );
}
